"use client"

import { trackOrder, type TrackResult } from "@lib/data/order-track"
import { CheckCircleSolid, Loader } from "@medusajs/icons"
import { clx } from "@modules/common/components/ui"
import { useSearchParams } from "next/navigation"
import { useState } from "react"

export default function OrderTrackingForm() {
  const searchParams = useSearchParams()
  const [orderNumber, setOrderNumber] = useState(
    () => (searchParams.get("order") || "").toUpperCase()
  )
  const [identifier, setIdentifier] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<TrackResult | null>(null)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setLoading(true)
    setError(null)
    setResult(null)

    const res = await trackOrder(orderNumber, identifier)
    setLoading(false)

    if (res.success) {
      setResult(res.data)
    } else {
      setError(res.error)
    }
  }

  return (
    <div className="flex flex-col gap-8">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <label className="block">
          <span className="mb-2 block text-[12px] font-semibold uppercase leading-4 tracking-[0.1em] text-[#666666]">
            Order Number
          </span>
          <input
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
            placeholder="e.g. 2606K7Q9A"
            autoComplete="off"
            className="h-12 w-full rounded-none border border-[#D4D4D4] bg-transparent px-3 text-[16px] uppercase text-[#1A1A1A] outline-none transition-colors placeholder:normal-case placeholder:text-[#9AA0A6] focus:border-[#1A1A1A]"
          />
        </label>

        <label className="block">
          <span className="mb-2 block text-[12px] font-semibold uppercase leading-4 tracking-[0.1em] text-[#666666]">
            Email or Mobile Number
          </span>
          <input
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder="you@example.com or 01XXXXXXXXX"
            autoComplete="off"
            className="h-12 w-full rounded-none border border-[#D4D4D4] bg-transparent px-3 text-[16px] text-[#1A1A1A] outline-none transition-colors placeholder:text-[#9AA0A6] focus:border-[#1A1A1A]"
          />
        </label>

        <button
          type="submit"
          disabled={loading}
          className="mt-1 inline-flex h-12 w-full items-center justify-center gap-2 rounded-none bg-[#1A1A1A] px-8 text-xs font-semibold uppercase tracking-[0.16em] text-white transition-colors hover:bg-[#3D5240] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading && <Loader className="h-4 w-4 animate-spin" />}
          {loading ? "Searching..." : "Track Order"}
        </button>

        {error && (
          <p className="text-sm font-medium text-red-600">{error}</p>
        )}
      </form>

      {result && <TrackingResult result={result} />}
    </div>
  )
}

function TrackingResult({ result }: { result: TrackResult }) {
  const placedAt = result.placed_at
    ? new Date(result.placed_at).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : ""

  return (
    <div className="border border-[#E5E5E5] bg-white p-6 small:p-8">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-[#E5E5E5] pb-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#666666]">
            Order
          </p>
          <p className="alif-serif text-[20px] font-medium text-[#1A1A1A]">
            {result.order_number}
          </p>
        </div>
        {placedAt && (
          <p className="text-sm text-[#666666]">Placed {placedAt}</p>
        )}
      </div>

      {result.canceled ? (
        <div className="mt-6">
          <p className="text-sm font-medium text-red-600">
            This order has been cancelled.
          </p>
          {result.cancel_reason && (
            <p className="mt-1 text-sm text-[#666666]">
              Reason: {result.cancel_reason}
            </p>
          )}
          <p className="mt-2 text-sm text-[#666666]">
            If a payment was made, any refund is processed separately. Please
            contact support if you need help.
          </p>
        </div>
      ) : (
        <ol className="mt-7 flex flex-col gap-0">
          {result.steps.map((step, index) => {
            const isLast = index === result.steps.length - 1
            return (
              <li key={step.key} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <span
                    className={clx(
                      "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border",
                      step.done
                        ? "border-[#3D5240] bg-[#3D5240] text-white"
                        : "border-[#D4D4D4] bg-white text-[#9AA0A6]"
                    )}
                  >
                    {step.done ? (
                      <CheckCircleSolid className="h-4 w-4" />
                    ) : (
                      <span className="text-[11px] font-semibold">
                        {index + 1}
                      </span>
                    )}
                  </span>
                  {!isLast && (
                    <span
                      className={clx(
                        "my-1 w-px flex-1",
                        step.done ? "bg-[#3D5240]" : "bg-[#E5E5E5]"
                      )}
                      style={{ minHeight: "28px" }}
                    />
                  )}
                </div>
                <div className={clx("pb-6", isLast && "pb-0")}>
                  <p
                    className={clx(
                      "text-[15px] font-medium",
                      step.done ? "text-[#1A1A1A]" : "text-[#9AA0A6]"
                    )}
                  >
                    {step.label}
                  </p>
                  {step.done && step.at && (
                    <p className="mt-0.5 text-xs text-[#666666]">
                      {new Date(step.at).toLocaleString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  )}
                </div>
              </li>
            )
          })}
        </ol>
      )}

      {result.items.length > 0 && (
        <div className="mt-6 border-t border-[#E5E5E5] pt-5">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#666666]">
            Items
          </p>
          <ul className="flex flex-col gap-2">
            {result.items.map((item, index) => (
              <li
                key={index}
                className="flex items-baseline justify-between gap-3 text-sm text-[#1A1A1A]"
              >
                <span className="min-w-0 truncate">{item.title}</span>
                <span className="shrink-0 text-[#666666]">×{item.quantity}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
